import { Link } from 'react-router-dom';
import { UserPlus, PlayCircle, TrendingUp, Star, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const steps = [
  {
    icon: UserPlus,
    title: '1. Ücretsiz Başla',
    desc: 'Üye ol, ilk 2 dersi tamamen ücretsiz izle. Kredi kartı gerekmez.',
  },
  {
    icon: PlayCircle,
    title: '2. Planını Seç',
    desc: 'Kısa sap veya uzun sap bağlama için sana uygun eğitim planını seç.',
  },
  {
    icon: TrendingUp,
    title: '3. Adım Adım Öğren',
    desc: '200+ video ders, nota arşivi ve akort cihazı ile her gün gelişimini gör.',
  },
];

export const HowItWorks = () => (
  <section className="py-24" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
          3 Adımda <span className="gradient-text">Bağlama Öğren</span>
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Türk Halk Müziği yolculuğuna bugün başla. Hiç bilmeyenler için sıfırdan, çalanlar için ileri seviye eğitim.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {steps.map((step, i) => (
          <div key={i} className="relative glass rounded-2xl p-8 border border-white/10 hover:border-primary/40 transition-colors text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-5">
              <step.icon className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">{step.title}</h3>
            <p className="text-muted-foreground text-sm">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const testimonials = [
  {
    name: 'Zafer K.',
    location: 'Trabzon',
    text: 'Hiç nota bilmeden başladım. 3 ay sonra ilk türkümü çaldım. Dersler çok net anlatılıyor, tekrar tekrar izleyebilmek büyük avantaj.',
    stars: 5,
  },
  {
    name: 'Ömer A.',
    location: 'Güney Afrika',
    text: 'Yurt dışında yaşıyorum, bağlama hocası bulmak imkansızdı. Bu platform sayesinde uzun sap bağlamayı evimden öğreniyorum.',
    stars: 5,
  },
  {
    name: 'Eymen Rona E.',
    location: 'Mersin',
    text: 'Akort cihazı ve metronom derslerle birlikte gelince ekstra uygulama aramama gerek kalmadı. Kısa sap dersleri harika ilerliyor.',
    stars: 5,
  },
];

export const Testimonials = () => (
  <section className="py-24 bg-gradient-to-b from-transparent via-primary/5 to-transparent" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
          Öğrencilerimiz <span className="gradient-text">Ne Diyor?</span>
        </h2>
        <p className="text-muted-foreground">Türkiye'den ve dünyadan 1000+ öğrenci bizimle bağlama öğreniyor.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {testimonials.map((item, i) => (
          <div key={i} className="glass rounded-2xl p-6 border border-white/10" data-testid={`testimonial-card-${i}`}>
            <div className="flex gap-1 mb-4">
              {[...Array(item.stars)].map((_, s) => (
                <Star key={s} className="w-4 h-4 fill-primary text-primary" />
              ))}
            </div>
            <p className="text-sm text-white/80 mb-6 leading-relaxed">"{item.text}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-semibold text-primary">
                {item.name.charAt(0)}
              </div>
              <div>
                <div className="font-semibold text-sm">{item.name}</div>
                <div className="text-xs text-muted-foreground">{item.location}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const faqs = [
  {
    q: 'Hiç bağlama çalmayı bilmiyorum, öğrenebilir miyim?',
    a: 'Kesinlikle! Derslerimiz sıfırdan başlar: bağlama tutuşu, mızrap kullanımı, nota yerleri ve ilk türkünüze kadar adım adım ilerlersiniz. İlk 2 ders ücretsizdir, hemen deneyebilirsiniz.',
  },
  {
    q: 'Kısa sap mı uzun sap bağlama eğitimi mi almalıyım?',
    a: 'Her ikisi için de ayrı ders serilerimiz var. Yeni başlayanlara genellikle kısa sap bağlama önerilir çünkü perde aralıkları daha geniştir. Hangi bağlamayı almanız gerektiğinden emin değilseniz ücretsiz Saz Asistanı size yardımcı olur.',
  },
  {
    q: 'Dersler nasıl işleniyor?',
    a: '200+ video ders ile kendi hızınızda ilerlersiniz. Müzik teorisi, teknik egzersizler, solfej ve türkü çalışmaları kategorilere ayrılmıştır. Dersleri dilediğiniz kadar tekrar izleyebilirsiniz.',
  },
  {
    q: 'Bağlama akort cihazı gerçekten ücretsiz mi?',
    a: 'Evet! Online bağlama akort cihazımız ve metronomumuz tamamen ücretsizdir. Mikrofonunuzla çalışır; kısa sap, uzun sap, kara düzen, bozuk düzen ve misket düzeni gibi tüm yaygın düzenleri destekler.',
  },
  {
    q: 'Aboneliğimi istediğim zaman iptal edebilir miyim?',
    a: 'Evet, taahhüt yoktur. Aboneliğinizi istediğiniz zaman iptal edebilirsiniz. Ödemeleriniz Stripe altyapısı ile güvenle işlenir.',
  },
  {
    q: 'Mobil uygulamanız var mı?',
    a: 'Evet! DersJet uygulamamız iOS (App Store) ve Android (Google Play) mağazalarında ücretsiz olarak yayında. Bağlama dersleri, akort ve metronom cebinizde.',
  },
];

export const FAQSection = () => (
  <section className="py-24" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm mb-4">
          <HelpCircle className="w-4 h-4" />
          Sıkça Sorulan Sorular
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold">
          Merak <span className="gradient-text">Edilenler</span>
        </h2>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((faq, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="glass rounded-xl border border-white/10 px-5 data-[state=open]:border-primary/40"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger className="text-left text-sm md:text-base font-medium hover:no-underline">
              {faq.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              {faq.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="text-center text-sm text-muted-foreground mt-8">
        Başka sorunuz mu var?{' '}
        <a href="mailto:destek@baglamasatinal.com" className="text-primary hover:underline">destek@baglamasatinal.com</a>
        {' '}adresinden bize ulaşın veya{' '}
        <Link to="/satin-al" className="text-primary hover:underline">Saz Asistanı</Link>'na sorun.
      </p>
    </div>
  </section>
);
